/**
 * Página de Gerenciamento de Templates WhatsApp
 * Sprint 2 - Notificame Integration
 * 
 * Permite gerenciar templates de mensagens para WhatsApp Oficial
 * Templates são obrigatórios para iniciar conversas fora da janela de 24h
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Loader2, 
  FileText, 
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  Pause,
  Filter,
  LayoutTemplate,
  LayoutGrid,
  List,
  Tag,
  Globe,
  X
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { toast } from 'sonner';
import { TemplateCard } from '@/components/tenant/templates/TemplateCard';
import { TemplateListRow } from '@/components/tenant/templates/TemplateListRow';
import { CreateTemplateDialog } from '@/components/tenant/templates/CreateTemplateDialog';
import { EditTemplateDialog } from '@/components/tenant/templates/EditTemplateDialog';
import { TemplatePreviewDialog } from '@/components/tenant/templates/TemplatePreviewDialog';
import { ClassificationManagerDialog } from '@/components/tenant/templates/ClassificationManagerDialog';
import { SyncTemplateInboxDialog } from '@/components/tenant/templates/SyncTemplateInboxDialog';
// Visibilidade unificada
import ManageVisibilityDialog from '@/components/shared/ManageVisibilityDialog';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';
import { TemplatesPaywall } from '@/components/tenant/TemplatesPaywall';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

// Interface para classificação de template
interface TemplateClassification {
  id: string;
  name: string;
  color: string;
  description?: string;
}

// Interface para template
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejection_reason: string | null;
  components: any;
  variables: any;
  example_values: any;
  provider_metadata: any;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  classification_id: string | null;
  inbox?: {
    name: string;
    identifier: string;
  } | null;
  classification?: {
    id: string;
    name: string;
    color: string;
  } | null;
  visibility?: EntityVisibility | null;
}

// Interface para inbox WhatsApp Oficial (com provider)
interface OfficialInbox {
  id: string;
  name: string;
  identifier: string;
  provider_id?: string;
  provider_slug?: string;
  provider_name?: string;
}

// Configuração de status
const statusConfig: Record<string, { label: string; icon: typeof CheckCircle; color: string }> = {
  APPROVED: { label: 'Aprovado', icon: CheckCircle, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  PENDING: { label: 'Pendente', icon: Clock, color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  REJECTED: { label: 'Rejeitado', icon: XCircle, color: 'bg-destructive/10 text-destructive border-destructive/20' },
  PAUSED: { label: 'Pausado', icon: Pause, color: 'bg-muted text-muted-foreground border-muted' },
};

// Configuração de categoria
const categoryConfig: Record<string, { label: string; color: string }> = {
  MARKETING: { label: 'Marketing', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  UTILITY: { label: 'Utilitário', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  AUTHENTICATION: { label: 'Autenticação', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
};

/**
 * Componente de métricas para KPI cards
 */
/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant: 'default' | 'success' | 'warning' | 'error';
}

/** Mapeamento de variantes para cores (padrão DealListKpiCards) */
const variantStyles = {
  default: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  warning: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
  error: { cardBg: 'bg-pink-100 dark:bg-pink-900/30', iconColor: 'text-pink-700 dark:text-pink-300' },
};

/** Card de KPI compacto horizontal (padrão unificado DealListKpiCards) */
const MetricCard = ({ title, value, icon: Icon, variant }: MetricCardProps) => (
  <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', variantStyles[variant].cardBg)}>
    <CardContent className="p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('h-5 w-5 shrink-0', variantStyles[variant].iconColor)} />
          <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
        </div>
        <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
      </div>
    </CardContent>
  </Card>
);

/**
 * Página principal de gerenciamento de templates
 */
// Tipo de visualização
type ViewMode = 'cards' | 'list';

const TenantTemplates = () => {
  const { currentTenant } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { hasOfficialWhatsApp, isLoading: isLoadingLimits, limits } = useResourceLimits();
  
  // Estados
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [officialInboxes, setOfficialInboxes] = useState<OfficialInbox[]>([]);
  const [classifications, setClassifications] = useState<TemplateClassification[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [inboxFilter, setInboxFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
const [classificationFilter, setClassificationFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  
  // Estado de visualização com persistência
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('templates_viewMode');
    return (saved === 'cards' || saved === 'list') ? saved : 'cards';
  });
  
  // Estados de dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setCreateDialogOpen(true));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [classificationManagerOpen, setClassificationManagerOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  // Visibilidade unificada (v1.0.0 - 2026-06-25)
  const [visibilityTemplate, setVisibilityTemplate] = useState<WhatsAppTemplate | null>(null);
  
  // Handler para mudança de visualização
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('templates_viewMode', mode);
  };

  // Carrega dados ao montar
  useEffect(() => {
    if (currentTenant?.id) {
      fetchOfficialInboxes();
      fetchTemplates();
      fetchClassifications();
    }
  }, [currentTenant?.id]);

  /**
   * Busca classificações do tenant
   */
  const fetchClassifications = async () => {
    if (!currentTenant?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('template_classifications')
        .select('id, name, color, description')
        .eq('tenant_id', currentTenant.id)
        .order('name');

      if (error) throw error;
      setClassifications(data || []);
    } catch (error) {
      logger.error('[Templates] Erro ao buscar classificações:', error);
    }
  };

  /**
   * Busca inboxes do tipo whatsapp_oficial com informações do provider
   */
  const fetchOfficialInboxes = async () => {
    if (!currentTenant?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('inboxes')
        .select(`
          id, 
          name, 
          identifier,
          provider_id,
        api_providers (
          id,
          slug,
          name,
          display_name
        )
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('channel_type', 'whatsapp_oficial');

      if (error) throw error;
      
      // Mapeia para incluir dados do provider
      const mappedInboxes: OfficialInbox[] = (data || []).map(inbox => ({
        id: inbox.id,
        name: inbox.name,
        identifier: inbox.identifier,
        provider_id: inbox.provider_id || undefined,
        provider_slug: (inbox.api_providers as any)?.slug || undefined,
        // Usa display_name (apelido) com fallback para name técnico
        provider_name: (inbox.api_providers as any)?.display_name || (inbox.api_providers as any)?.name || undefined,
      }));
      
      setOfficialInboxes(mappedInboxes);
    } catch (error) {
      logger.error('[Templates] Erro ao buscar inboxes oficiais:', error);
    }
  };

  /**
   * Busca templates do banco de dados
   */
  const fetchTemplates = async () => {
    if (!currentTenant?.id) return;
    
    try {
      setLoading(true);
      
      // Busca inboxes do tenant primeiro
      const { data: inboxes, error: inboxError } = await supabase
        .from('inboxes')
        .select('id')
        .eq('tenant_id', currentTenant.id)
        .eq('channel_type', 'whatsapp_oficial');

      if (inboxError) throw inboxError;
      
      if (!inboxes || inboxes.length === 0) {
        setTemplates([]);
        return;
      }

      const inboxIds = inboxes.map(i => i.id);
      
      // Busca templates das inboxes com classificação
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select(`
          *,
          inbox:inboxes (
            name,
            identifier
          ),
          classification:template_classifications (
            id,
            name,
            color
          )
        `)
        .in('inbox_id', inboxIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setTemplates((data || []) as WhatsAppTemplate[]);
      logger.info('[Templates]', `${data?.length || 0} templates carregados`);
    } catch (error) {
      logger.error('[Templates] Erro ao buscar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sincroniza templates com API do provider
   * Suporta filtro por inbox ou provider específico
   */
  /**
   * Sincroniza templates com API do provider.
   * Recebe inboxId selecionado no dialog: inbox específica ou 'all'.
   */
  const handleSyncTemplates = async (inboxId: string | 'all') => {
    if (officialInboxes.length === 0) {
      toast.warning('Nenhuma inbox WhatsApp Oficial configurada');
      return;
    }

    try {
      setSyncing(true);
      // Fecha o dialog ao iniciar
      setSyncDialogOpen(false);
      toast.info('Sincronizando templates...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Sessão expirada');
        return;
      }

      // Determina quais inboxes sincronizar baseado na seleção do dialog
      const inboxesToSync = inboxId === 'all'
        ? officialInboxes
        : officialInboxes.filter(i => i.id === inboxId);

      // Sincroniza para cada inbox selecionada
      let totalSynced = 0;
      let totalErrors = 0;
      
      for (const inbox of inboxesToSync) {
        const { data, error } = await supabase.functions.invoke('sync-templates', {
          body: { inbox_id: inbox.id },
        });

        if (error) {
          logger.error(`[Templates] Erro ao sincronizar ${inbox.name}:`, error);
          totalErrors++;
        } else if (data?.synced) {
          totalSynced += data.synced;
          logger.info('[Templates]', `${data.synced} templates sincronizados de ${inbox.name} (${inbox.provider_slug || 'provider desconhecido'})`);
        }
      }

      if (totalErrors > 0) {
        toast.warning(`${totalSynced} template(s) sincronizado(s), ${totalErrors} inbox(es) com erro`);
      } else {
        toast.success(`${totalSynced} template(s) sincronizado(s)`);
      }
      
      await fetchTemplates();
    } catch (error) {
      logger.error('[Templates] Erro ao sincronizar:', error);
      toast.error('Erro ao sincronizar templates');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handlers de ações
   */
  const handleEdit = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setEditDialogOpen(true);
  };

  const handlePreview = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setPreviewDialogOpen(true);
  };

  const handleDelete = async (template: WhatsAppTemplate) => {
    try {
      const { error } = await supabase
        .from('whatsapp_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;
      
      toast.success('Template excluído');
      await fetchTemplates();
    } catch (error) {
      logger.error('[Templates] Erro ao excluir:', error);
      toast.error('Erro ao excluir template');
    }
  };

  const handleSuccess = () => {
    fetchTemplates();
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedTemplate(null);
  };

  // Mapa de idiomas para labels amigáveis
  const languageLabels: Record<string, string> = {
    'pt_BR': 'Português (BR)',
    'en_US': 'Inglês (US)',
    'en': 'Inglês',
    'es': 'Espanhol',
    'es_ES': 'Espanhol (ES)',
    'es_AR': 'Espanhol (AR)',
    'es_MX': 'Espanhol (MX)',
  };

  const formatLanguageLabel = (code: string): string => {
    return languageLabels[code] || code;
  };

  // Verifica se há filtros ativos
  const hasActiveFilters = 
    searchQuery !== '' || 
    statusFilter !== 'all' || 
    categoryFilter !== 'all' || 
    inboxFilter !== 'all' || 
    providerFilter !== 'all' || 
    languageFilter !== 'all' ||
    classificationFilter !== 'all';

  // Limpa todos os filtros
  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setInboxFilter('all');
    setProviderFilter('all');
    setLanguageFilter('all');
    setClassificationFilter('all');
  };

  // Filtra templates (inclui filtro por provider e idioma)
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = searchQuery === '' || 
      t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    const matchesInbox = inboxFilter === 'all' || t.inbox_id === inboxFilter;
    const matchesLanguage = languageFilter === 'all' || t.language === languageFilter;
    
    // Filtro por provider - verifica no array de inboxes
    const inboxOfTemplate = officialInboxes.find(i => i.id === t.inbox_id);
    const matchesProvider = providerFilter === 'all' || inboxOfTemplate?.provider_slug === providerFilter;
    
    const matchesClassification = classificationFilter === 'all' || 
      (classificationFilter === 'none' ? !t.classification_id : t.classification_id === classificationFilter);
    return matchesSearch && matchesStatus && matchesCategory && matchesInbox && matchesProvider && matchesClassification && matchesLanguage;
  });

  // Estatísticas
  const approvedCount = templates.filter(t => t.status === 'APPROVED').length;
  const pendingCount = templates.filter(t => t.status === 'PENDING').length;
  const rejectedCount = templates.filter(t => t.status === 'REJECTED').length;

  // Paywall: exibe quando tenant não tem acesso ao WhatsApp Oficial (exclusivo Enterprise)
  if (!isLoadingLimits && !hasOfficialWhatsApp) {
    return <TemplatesPaywall currentPlan={limits?.plan_id} />;
  }

  if (loading || isLoadingLimits) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Empty state se não há inboxes oficiais
  if (officialInboxes.length === 0) {
    return (
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-background via-background to-muted/30 border p-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <LayoutTemplate className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Templates WhatsApp</h1>
          </div>
        </div>

        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nenhuma inbox WhatsApp Oficial</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Para gerenciar templates, primeiro configure uma inbox do tipo WhatsApp Oficial.
            </p>
            <Button className="mt-4" onClick={() => window.location.href = '/tenant/inboxes'}>
              Ir para Inboxes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Templates WhatsApp</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie templates de mensagens para WhatsApp Oficial
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="xs" onClick={() => setSyncDialogOpen(true)} disabled={syncing}>
            <RefreshCw className={cn("mr-1.5", syncing && "animate-spin")} />
            Sincronizar
          </Button>
          {canEdit('templates') && (
            <Button variant="outline" size="xs" onClick={() => setClassificationManagerOpen(true)}>
              <Tag className="mr-1.5" />
              Classificação
            </Button>
          )}
          {canCreate('templates') && (
            <Button size="xs" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-1.5" />
              Novo Template
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard title="Total" value={templates.length} icon={FileText} variant="default" />
        <MetricCard title="Aprovados" value={approvedCount} icon={CheckCircle} variant="success" />
        <MetricCard title="Pendentes" value={pendingCount} icon={Clock} variant="warning" />
        <MetricCard title="Rejeitados" value={rejectedCount} icon={XCircle} variant="error" />
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="APPROVED">Aprovados</SelectItem>
                <SelectItem value="PENDING">Pendentes</SelectItem>
                <SelectItem value="REJECTED">Rejeitados</SelectItem>
                <SelectItem value="PAUSED">Pausados</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Categorias</SelectItem>
                <SelectItem value="MARKETING">Marketing</SelectItem>
                <SelectItem value="UTILITY">Utilitário</SelectItem>
                <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro de Inbox - sempre visível se houver pelo menos 1 */}
            {officialInboxes.length >= 1 && (
              <Select value={inboxFilter} onValueChange={setInboxFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Inbox" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas Inboxes</SelectItem>
                  {officialInboxes.map(inbox => (
                    <SelectItem key={inbox.id} value={inbox.id}>
                      {inbox.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Filtro de Provider - sempre visível se houver pelo menos 1 */}
            {(() => {
              const uniqueProviders = [...new Set(officialInboxes.map(i => i.provider_slug).filter(Boolean))];
              return uniqueProviders.length >= 1 ? (
                <Select value={providerFilter} onValueChange={setProviderFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Providers</SelectItem>
                    {uniqueProviders.map(slug => {
                      const providerName = officialInboxes.find(i => i.provider_slug === slug)?.provider_name || slug;
                      return (
                        <SelectItem key={slug} value={slug!}>
                          {providerName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : null;
            })()}

            {/* Filtro de Idioma */}
            {(() => {
              const uniqueLanguages = [...new Set(templates.map(t => t.language).filter(Boolean))];
              return uniqueLanguages.length >= 1 ? (
                <Select value={languageFilter} onValueChange={setLanguageFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Globe className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Idioma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Idiomas</SelectItem>
                    {uniqueLanguages.map(lang => (
                      <SelectItem key={lang} value={lang}>
                        {formatLanguageLabel(lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null;
            })()}

            {/* Filtro de Classificação */}
            <Select value={classificationFilter} onValueChange={setClassificationFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Classificação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Classificações</SelectItem>
                <SelectItem value="none">Sem Classificação</SelectItem>
                {classifications.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full shrink-0" 
                        style={{ backgroundColor: c.color }} 
                      />
                      {c.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Botão Limpar Filtros */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground h-10"
              >
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}

            {/* Toggle Card/Lista */}
            <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border">
              <button
                onClick={() => handleViewModeChange('cards')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  viewMode === 'cards'
                    ? 'bg-foreground text-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title="Visualizar em cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  viewMode === 'list'
                    ? 'bg-foreground text-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title="Visualizar em lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Templates */}
      {filteredTemplates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold text-lg mb-2">
              {templates.length === 0 ? 'Nenhum template cadastrado' : 'Nenhum template encontrado'}
            </h3>
            <p className="text-muted-foreground text-sm">
              {templates.length === 0 
                ? 'Clique em "Novo Template" para criar ou "Sincronizar" para importar.'
                : 'Ajuste os filtros para encontrar o que procura.'
              }
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map(template => {
            const inboxOfTemplate = officialInboxes.find(i => i.id === template.inbox_id);
            return (
              <TemplateCard
                key={template.id}
                template={template}
                providerName={inboxOfTemplate?.provider_name}
                statusConfig={statusConfig}
                categoryConfig={categoryConfig}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onDelete={handleDelete}
                onManageVisibility={setVisibilityTemplate}
                canEdit={canEdit('templates')}
                canDelete={canDelete('templates')}
              />
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredTemplates.map(template => {
                const inboxOfTemplate = officialInboxes.find(i => i.id === template.inbox_id);
                return (
                  <TemplateListRow
                    key={template.id}
                    template={template}
                    providerName={inboxOfTemplate?.provider_name}
                    statusConfig={statusConfig}
                    categoryConfig={categoryConfig}
                    onEdit={handleEdit}
                    onPreview={handlePreview}
                    onDelete={handleDelete}
                    onManageVisibility={setVisibilityTemplate}
                    canEdit={canEdit('templates')}
                    canDelete={canDelete('templates')}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <CreateTemplateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        inboxes={officialInboxes}
        onSuccess={handleSuccess}
      />

      {selectedTemplate && (
        <>
          <EditTemplateDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            template={selectedTemplate}
            onSuccess={handleSuccess}
          />
          <TemplatePreviewDialog
            open={previewDialogOpen}
            onOpenChange={setPreviewDialogOpen}
            template={selectedTemplate}
          />
        </>
      )}

      {/* Dialog de gerenciamento de classificações */}
      <ClassificationManagerDialog
        open={classificationManagerOpen}
        onOpenChange={setClassificationManagerOpen}
        tenantId={currentTenant?.id}
        onUpdate={fetchClassifications}
      />

      {/* Dialog de seleção de inbox para sincronização */}
      <SyncTemplateInboxDialog
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        inboxes={officialInboxes}
        onConfirm={handleSyncTemplates}
        isSyncing={syncing}
      />

      {/* Visibilidade unificada (Listas/Campanhas/Templates) */}
      <ManageVisibilityDialog
        open={!!visibilityTemplate}
        onOpenChange={(open) => !open && setVisibilityTemplate(null)}
        entityType="whatsapp_template"
        entityId={visibilityTemplate?.id}
        entityLabel={visibilityTemplate?.name}
      />
    </div>
  );
};

export default TenantTemplates;
